import React from 'react';
import {
  Alert,
  Amount,
  Badge,
  Box,
  Button,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  useToast,
} from '@razorpay/blade/components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from 'api';
import { AppError, getAppErrorDetails } from 'utils/AppError';
import {
  EMI_STATUS_COLOR,
  EMI_STATUS_LABEL,
  SKIP_LOAN_DETAIL_KEY,
  SKIP_PREVIEW_KEY,
} from './constants';
import { formatMonthYearShort, normalizeLoanDetail } from './utils';
import {
  SkipEmiModalProps,
  LoanDetail,
  RecoveryScheduleEntry,
  RecoveryScheduleTableNode,
} from './types';
import LoanRequestSuccess from 'assets/loan/loan-request-success.png';
import { overlayLayers } from 'constants/overlayLayers.generated';

export const SkipEmiModal = ({
  isOpen,
  onDismiss,
  loanId,
  loan,
  onSuccess,
  activeDrawerQueryKey,
  recoveryPreviewQueryKey,
}: SkipEmiModalProps) => {
  const queryClient = useQueryClient();
  const { show: showToast } = useToast();
  const [skipOption, setSkipOption] = React.useState<'1' | '2'>('1');
  const [isSuccessOpen, setIsSuccessOpen] = React.useState(false);
  const [skippedMonth, setSkippedMonth] = React.useState<string | null>(null);

  const { data: loanDetailData } = useQuery({
    queryKey: [SKIP_LOAN_DETAIL_KEY, loanId],
    queryFn: () => api.loans.getLoanDetail(loanId!),
    enabled: isOpen && !!loanId,
  });
  const loanDetail = normalizeLoanDetail<LoanDetail>(loanDetailData);
  const nextEmiMonth = loanDetail?.next_emi_month ?? loan?.next_emi_month;

  const {
    data: skipPreviewData,
    isError: isPreviewError,
    error: previewError,
    isFetching: isPreviewFetching,
  } = useQuery({
    queryKey: [SKIP_PREVIEW_KEY, loanId, skipOption, nextEmiMonth],
    queryFn: () =>
      api.loans.recoveryPreview(
        loanId!,
        'skip-emi',
        Number(skipOption),
        undefined,
        undefined,
        nextEmiMonth,
      ),
    enabled: isOpen && !!loanId && !!nextEmiMonth,
    retry: false,
  });
  const previewErrorMessage = isPreviewError
    ? (previewError as Error)?.message || 'Failed to load preview.'
    : null;
  const rawSchedule: RecoveryScheduleEntry[] = skipPreviewData?.data ?? [];
  const skipSchedule: RecoveryScheduleTableNode[] = rawSchedule.map((entry, idx) => ({
    ...entry,
    id: entry.id ?? idx,
  }));

  const skipEmiMutation = useMutation({
    mutationFn: () =>
      api.loans.editLoan(
        { action: 'skip-emi', option: Number(skipOption), payroll_month: nextEmiMonth },
        loanId!,
      ),
    onSuccess: (res) => {
      const skipped =
        (res as { skipped_months?: string[]; skipped_month?: string })?.skipped_months?.[0] ??
        (res as { skipped_month?: string })?.skipped_month ??
        nextEmiMonth ??
        null;
      setSkippedMonth(skipped);
      queryClient.invalidateQueries(['LOANS']);
      queryClient.invalidateQueries(activeDrawerQueryKey);
      queryClient.invalidateQueries(recoveryPreviewQueryKey);
      onDismiss();
      setIsSuccessOpen(true);
    },
    onError: (error: typeof AppError) => {
      const { messageForUser } = getAppErrorDetails(error);
      showToast({
        content: messageForUser || 'Failed to skip EMI. Please try again.',
        color: 'negative',
        duration: 5000,
      });
    },
  });

  const handleSuccessClose = () => {
    setIsSuccessOpen(false);
    showToast({ content: 'EMI successfully skipped!', color: 'positive', duration: 4000 });
    onSuccess();
  };

  return (
    <>
      <Modal isOpen={isOpen} onDismiss={onDismiss} size="full" zIndex={overlayLayers.modal}>
        <ModalHeader leading="Skip EMI" />
        <ModalBody height="100%">
          <Box
            display="flex"
            justifyContent="center"
            alignItems="flex-start"
            height="100%"
            overflowY="auto"
            paddingY="spacing.7">
            <Box
              display="flex"
              flexDirection="column"
              width="600px"
              backgroundColor="surface.background.gray.intense"
              borderWidth="thin"
              borderRadius="medium"
              borderColor="surface.border.gray.subtle"
              gap="spacing.8">
              <Box display="flex" flexDirection="column" gap="spacing.8">
                <Box
                  paddingX="spacing.7"
                  paddingTop="spacing.8"
                  display="flex"
                  flexDirection="column"
                  gap="spacing.8">
                  <Box display="flex" flexDirection="column" gap="spacing.2">
                    <Heading size="medium" weight="semibold" color="surface.text.gray.normal">
                      EMI Details
                    </Heading>
                    <Text size="small" color="surface.text.gray.subtle">
                      EMI amount of ₹
                      {(loanDetail?.monthly_total_emi ?? loan?.monthly_total_emi)?.toLocaleString(
                        'en-IN',
                      ) ?? '—'}{' '}
                      for the next payroll month will be skipped for the merchant
                    </Text>
                  </Box>
                  <Box>
                    <Text
                      size="small"
                      variant="body"
                      weight="medium"
                      color="surface.text.gray.muted"
                      marginBottom="spacing.3">
                      How does skipping this EMI impact future payments?
                    </Text>
                    <RadioGroup
                      label=""
                      value={skipOption}
                      onChange={({ value }) => setSkipOption(value as '1' | '2')}>
                      <Box display="flex" flexDirection="column" gap="spacing.3">
                        <Box
                          borderWidth="thin"
                          borderRadius="medium"
                          borderColor={
                            skipOption === '1'
                              ? 'surface.border.primary.normal'
                              : 'surface.border.gray.subtle'
                          }
                          padding="spacing.4">
                          <Radio
                            value="1"
                            helpText="Distribute the skipped amount across remaining EMIs, increasing each future EMI slightly.">
                            Increase EMI Amount
                          </Radio>
                        </Box>
                        <Box
                          borderWidth="thin"
                          borderRadius="medium"
                          borderColor={
                            skipOption === '2'
                              ? 'surface.border.primary.normal'
                              : 'surface.border.gray.subtle'
                          }
                          padding="spacing.4">
                          <Radio
                            value="2"
                            helpText="Keep EMI amounts unchanged and add an extra month to recover the skipped payment.">
                            Extend Number of EMIs
                          </Radio>
                        </Box>
                      </Box>
                    </RadioGroup>
                  </Box>
                </Box>

                {isPreviewFetching && (
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    paddingY="spacing.6">
                    <Spinner accessibilityLabel="Loading preview" size="medium" />
                  </Box>
                )}

                {!isPreviewFetching && previewErrorMessage && (
                  <Box paddingX="spacing.7">
                    <Alert
                      color="negative"
                      isDismissible={false}
                      isFullWidth
                      description={previewErrorMessage}
                    />
                  </Box>
                )}

                {!isPreviewFetching && !isPreviewError && skipSchedule.length > 0 && (
                  <Box paddingX="spacing.7" display="flex" flexDirection="column">
                    <Heading size="medium" weight="semibold" marginBottom="spacing.4">
                      Preview
                    </Heading>
                    <Table<RecoveryScheduleTableNode>
                      height="290px"
                      data={{ nodes: skipSchedule }}
                      gridTemplateColumns="1fr 1fr 1fr 1fr">
                      {(rows) => (
                        <>
                          <TableHeader>
                            <TableHeaderRow>
                              <TableHeaderCell>Month</TableHeaderCell>
                              <TableHeaderCell textAlign="right">Old EMI</TableHeaderCell>
                              <TableHeaderCell textAlign="right">New EMI</TableHeaderCell>
                              <TableHeaderCell>Status</TableHeaderCell>
                            </TableHeaderRow>
                          </TableHeader>
                          <TableBody>
                            {rows.map((entry) => (
                              <TableRow key={entry.id} item={entry}>
                                <TableCell>
                                  <Text size="small">
                                    {formatMonthYearShort(entry.payroll_month ?? entry.month)}
                                  </Text>
                                </TableCell>
                                <TableCell textAlign="right">
                                  <Amount
                                    value={
                                      entry.status === 1
                                        ? entry.emi_amount ?? entry.total_emi ?? 0
                                        : loanDetail?.monthly_total_emi ??
                                          loan?.monthly_total_emi ??
                                          0
                                    }
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell textAlign="right">
                                  <Amount
                                    value={entry.emi_amount ?? entry.total_emi ?? 0}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Badge
                                    color={EMI_STATUS_COLOR[entry.status] ?? 'notice'}
                                    size="small">
                                    {EMI_STATUS_LABEL[entry.status] ?? 'Pending'}
                                  </Badge>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </>
                      )}
                    </Table>
                  </Box>
                )}
              </Box>

              <Box
                borderTopWidth="thin"
                borderTopColor="surface.border.gray.muted"
                display="flex"
                justifyContent="space-between"
                width="100%"
                paddingX="spacing.8"
                paddingY="spacing.5">
                <Button variant="secondary" onClick={onDismiss}>
                  Close
                </Button>
                <Button
                  isLoading={skipEmiMutation.isLoading}
                  isDisabled={!!previewErrorMessage}
                  onClick={() => skipEmiMutation.mutate()}>
                  Next
                </Button>
              </Box>
            </Box>
          </Box>
        </ModalBody>
      </Modal>

      <Modal
        isOpen={isSuccessOpen}
        onDismiss={handleSuccessClose}
        size="small"
        zIndex={overlayLayers.modal}>
        <ModalHeader />
        <ModalBody>
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            gap="spacing.3"
            paddingY="spacing.4"
            textAlign="center">
            <Box display="flex" flexDirection="column" gap="spacing.6">
              <img src={LoanRequestSuccess} alt="Success" width={80} height={80} />
              {loanId && (
                <Text size="small" variant="body" weight="regular" color="surface.text.gray.muted">
                  ID #{loanId}
                </Text>
              )}
            </Box>
            <Box display="flex" flexDirection="column" gap="spacing.3">
              <Heading size="small" weight="semibold" color="surface.text.gray.normal">
                EMI Skipped!
              </Heading>
              <Text size="small" color="surface.text.gray.muted">
                The employee's EMI of ₹
                {(loanDetail?.monthly_total_emi ?? loan?.monthly_total_emi)?.toLocaleString(
                  'en-IN',
                ) ?? '—'}{' '}
                for {skippedMonth ? formatMonthYearShort(skippedMonth) : '—'} has been skipped.
              </Text>
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter>
          <Button isFullWidth variant="secondary" onClick={handleSuccessClose}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
};
